/**
 * Purpose: Displays the dice roll result with video playback or CSS fallback animation.
 * Owns: Dice video element, placeholder spin animation, result number display.
 * Notes: Six pre-rendered videos (dice-1.mp4 through dice-6.mp4) will be added later.
 *        Until then, a CSS animation shows a spinning dice that lands on the number.
 *        The dice value determines point value for the current question.
 */

import { useEffect, useRef, useState } from 'react';
import type { DiceValue } from '../../engine/diceRoller';
import { getDiceVideoPath } from '../../engine/diceRoller';

interface DiceRollProps {
  /** The dice value to display (1-6), or null if not yet rolled */
  value: DiceValue | null;
  /** Whether the roll animation is currently playing */
  isRolling: boolean;
  /** Callback when the roll animation/video completes */
  onRollComplete: () => void;
}

/** Duration of the CSS fallback spin animation in milliseconds */
const SPIN_DURATION_MS = 1500;

export function DiceRoll({ value, isRolling, onRollComplete }: DiceRollProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [useVideoFallback, setUseVideoFallback] = useState(true);
  const [animatingValue, setAnimatingValue] = useState<number | null>(null);

  /* Try to play the video when rolling starts; fall back to CSS if it fails */
  useEffect(() => {
    if (!isRolling || value === null) return;

    const video = videoRef.current;
    if (video && !useVideoFallback) {
      video.currentTime = 0;
      video.play().catch(() => {
        /* Video not available — use CSS fallback */
        setUseVideoFallback(true);
      });
    }

    if (useVideoFallback) {
      /* CSS fallback: cycle through numbers then land on the result */
      let frame = 0;
      const totalFrames = 15;
      const interval = setInterval(() => {
        frame++;
        setAnimatingValue(((frame % 6) + 1));
        if (frame >= totalFrames) {
          clearInterval(interval);
          setAnimatingValue(value);
          setTimeout(onRollComplete, SPIN_DURATION_MS / 3);
        }
      }, SPIN_DURATION_MS / totalFrames);

      return () => clearInterval(interval);
    }
  }, [isRolling, value, useVideoFallback, onRollComplete]);

  const displayValue = isRolling ? animatingValue : value;

  return (
    <div style={styles.container}>
      {/* Hidden video element — used when dice videos are available */}
      {value !== null && !useVideoFallback && (
        <video
          ref={videoRef}
          src={getDiceVideoPath(value)}
          style={styles.video}
          onEnded={onRollComplete}
          onError={() => setUseVideoFallback(true)}
          playsInline
          muted
        />
      )}

      {/* CSS fallback dice display */}
      {(useVideoFallback || !isRolling) && (
        <div
          style={{
            ...styles.diceFace,
            ...(isRolling ? styles.spinning : {}),
          }}
          role="img"
          aria-label={displayValue ? `Dice showing ${displayValue}` : 'Dice'}
        >
          <span style={styles.diceNumber}>
            {displayValue ?? '?'}
          </span>
        </div>
      )}

      {/* Point value label shown after roll completes */}
      {!isRolling && value !== null && (
        <p style={styles.pointLabel}>
          {value} {value === 1 ? 'point' : 'points'} on the line!
        </p>
      )}
    </div>
  );
}

/* _______________________________________________
 *              INLINE STYLES
 * _______________________________________________
 * All colors use CSS custom properties (theme tokens).
 * These will be extracted to CSS modules when the design matures.
 */

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem',
  },
  video: {
    width: '120px',
    height: '120px',
    borderRadius: '12px',
    objectFit: 'cover',
  },
  diceFace: {
    width: '100px',
    height: '100px',
    borderRadius: '16px',
    backgroundColor: 'var(--bg-card)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    border: '3px solid var(--accent-wood)',
  },
  spinning: {
    animation: 'pulse 0.15s ease-in-out infinite alternate',
  },
  diceNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: 'var(--text-dark)',
    fontFamily: 'var(--font-family-display)',
  },
  pointLabel: {
    fontSize: '1.05rem',
    color: 'var(--accent-gold)',
    fontWeight: '400',
    textAlign: 'center' as const,
    fontFamily: 'var(--font-family-handwriting)',
  },
};
